import React, { useEffect, useState } from 'react';
import {
  Container, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, CircularProgress, Stack,
  Box, Fab, Snackbar, Dialog, DialogTitle, DialogContent, Chip, TextField, TableSortLabel
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

const POAccessories = () => {
  const [allData, setAllData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogData, setDialogData] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'Po_date', direction: 'desc' });
  const navigate = useNavigate();

  const formatDateShort = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }); // dd/mmm
  };

  const getCompanyName = (id) => id === 1 ? 'A' : 'T';
  const getCompanyColor = (id) => id === 1 ? 'success' : 'warning';

  const handleDetails = (row) => {
    const filtered = allData.filter(r => r.Po_ID === row.Po_ID && r.CompanyID === row.CompanyID);
    setDialogData(filtered);
    setSelectedRow(row);
    setDialogOpen(true);
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/acc-po-approval-pending');
      const data = res.data;
      setAllData(data);
      const uniqueKeys = new Set();
      const unique = data.filter(item => {
        const key = `${item.Po_ID}-${item.CompanyID}`;
        return uniqueKeys.has(key) ? false : uniqueKeys.add(key);
      });
      setRows(unique);
    } catch (err) {
      setSnackbar({ open: true, message: 'Error fetching data' });
    }
    setLoading(false);
  };

  const filteredAndSortedRows = () => {
    const filtered = rows.filter(row =>
      row.PoNo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      row.Name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      row.BuyerOrderNo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      row.AccDesc?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const sorted = [...filtered].sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  };

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <Container maxWidth="sm" sx={{ py: 2, position: 'relative' }}>
      <Typography variant="h6" gutterBottom>Pending Accessories POs</Typography>

      <TextField
        fullWidth
        size="small"
        placeholder="Search PO No, Buyer Order or Accessory"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        sx={{ mb: 2 }}
      />

      {loading ? (
        <CircularProgress />
      ) : (
        <TableContainer component={Paper}>
          <Table size="small" sx={{ tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: '15%' }}>
                  <TableSortLabel
                    active={sortConfig.key === 'PoNo'}
                    direction={sortConfig.direction}
                    onClick={() => requestSort('PoNo')}
                  >
                    PO
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ width: '8%' }}>
                  <TableSortLabel
                    active={sortConfig.key === 'Po_date'}
                    direction={sortConfig.direction}
                    onClick={() => requestSort('Po_date')}
                  >
                    Date
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ width: '55%' }}>
                  <TableSortLabel sx={{ marginLeft: 2 }}
                    active={sortConfig.key === 'Name'}
                    direction={sortConfig.direction}
                    onClick={() => requestSort('Name')}
                  >
                    Supplier
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ width: '25%' }} align="right">
                  <TableSortLabel
                    active={sortConfig.key === 'Po_qty'}
                    direction={sortConfig.direction}
                    onClick={() => requestSort('Po_qty')}
                  >
                    Qty
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredAndSortedRows().map((row) => (
                <TableRow
                  key={`${row.Po_ID}-${row.CompanyID}`}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => handleDetails(row)}
                >
                  <TableCell>
                    <Stack spacing={0.5} alignItems="center">
                      <Chip
                        label={getCompanyName(row.CompanyID)}
                        size="small"
                        color={getCompanyColor(row.CompanyID)}
                      />
                      <Typography variant="caption">{row.PoNo}</Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>{formatDateShort(row.Po_date)}</TableCell>
                  <TableCell>
                    <Typography noWrap variant="body2" title={row.Name}>{row.Name}</Typography>
                    <Typography variant="caption" color="text.secondary" fontSize={10}>
                      {row.BuyerOrderNo.includes(',') ? 'MANY' : row.BuyerOrderNo}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">{row.Po_qty}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Fab color="primary" sx={{ position: 'fixed', bottom: 16, right: 16 }} onClick={() => navigate(-1)}>
        <ArrowBackIcon />
      </Fab>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
      />

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        PaperProps={{ sx: { mx: '4px', overflowY: 'auto', borderRadius: 2 } }}
      >
        <DialogTitle sx={{ backgroundColor: '#f3e5f5' }}>
          Accessory PO No {selectedRow?.PoNo} / {getCompanyName(selectedRow?.CompanyID)}
        </DialogTitle>
        <DialogContent>
          <Table size="small" sx={{ '& td, & th': { py: 0.25, px: 0.75 }, fontSize: '11px' }}>
            <TableHead>
              <TableRow>
                <TableCell>Buyer Order</TableCell>
                <TableCell>Accessory</TableCell>
                <TableCell>Color</TableCell>
                <TableCell>Size</TableCell>
                <TableCell>Qty</TableCell>
                <TableCell>Rate</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {dialogData.map((entry, i) => (
                <TableRow key={i}>
                  <TableCell>{entry.BuyerOrderNo}</TableCell>
                  <TableCell>{entry.AccDesc}</TableCell>
                  <TableCell>{entry.clr}</TableCell>
                  <TableCell>{entry.siz}</TableCell>
                  <TableCell>{entry.Po_qty}</TableCell>
                  <TableCell>{entry.Po_Rate}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default POAccessories;
